
import { BarsArrowUpIcon, ClockIcon, EnvelopeOpenIcon, PhoneIcon, UserIcon } from '@heroicons/react/24/solid';
import type { CustomFlowbiteTheme, TabsRef } from 'flowbite-react';
import { Tabs } from 'flowbite-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { InteractionFormData } from '../../../@types/contactInteraction';
import type { Owner, OwnerStepType } from '../../../@types/Owner';
import {  OWNER_STEPS  } from '../../../@types/Owner';
import { setCurrentStep } from '../../../store/DpeReducer';
import type { RootState } from '../../../store/store';
import Badge from '../../common/Badge';
import Spinner from '../../common/Spinner';
import WarningValidationPending from '../../common/WarningValidationPending';
import { CoproAffectationToggle, getStoredPreference } from '../components/CoproAffectationToggle';
import { DPE_QUALIF_STEPS } from '../components/DpeStepsQualif/dpeQualifStep';
import DueFilterChips from '../components/DueFilterChips';
import ProspectingOpportunitiesOwnerNavTabs from '../components/ProspectingOpportunitiesOwnerNavTabs';
import type { DueFilter } from '../utils/crmDeepLinks';
import { getDueFilters, parseDueFilter } from '../utils/crmDeepLinks';
import { CallbackRequestsTable, NotContactedTable, PendingTable, ToCallTable } from './components';
import DpeGeneralMailingsTable from './components/DpeGeneralMailings/DpeGeneralMailingsTable';
import { fetchOwnersByStep } from './utils/ownerStepApi';

interface TabData {
  status: AsyncStatus;
  data: Owner[];
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalResults: number;
  };
  sortField?: string;
}

// Custom theme for tabs
const customTheme: CustomFlowbiteTheme['tabs'] = {
  base: 'flex flex-col',
  tablist: {
    base: 'flex text-center',
    tabitem: {
      base: 'flex items-center justify-center rounded-t-lg p-4 text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 w-full md:w-auto',
      variant: {
        default: {
          base: 'text-gray-500 dark:text-gray-400',
          active: {
            on: 'active bg-gray-100 p-4 text-rgpd-blue',
            off: 'bg-white hover:bg-gray-50 hover:text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-white',
          },
        },
      },
    },
  },
};

// Define the tabs and their corresponding steps
const TABS = [
  { 
    id: 'non-contacte', 
    title: 'Non contacté', 
    step: OWNER_STEPS.NOT_CONTACTED, 
    icon: UserIcon, 
  },
  { 
    id: 'en-attente', 
    title: 'En attente de retour', 
    step: OWNER_STEPS.WAITING_FOR_FEEDBACK, 
    icon: ClockIcon, 
  },
  { 
    id: 'a-appeler', 
    title: 'A appeler', 
    step: OWNER_STEPS.TO_CALL, 
    icon: PhoneIcon, 
  },
  { 
    id: 'demandes-rappel', 
    title: 'Demandes de rappels à traiter', 
    step: OWNER_STEPS.TO_RECALL, 
    icon: BarsArrowUpIcon, 
  },
];

const DPE_GENERAL_MAILING_TAB_ID = 'envois-generaux-dpe';
const RECALL_TAB_ID = 'demandes-rappel';

function OwnerCommunication() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const dispatch = useDispatch();
  const location = useLocation();

  const { initialTabId, initialCoproId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const coproIdParam = params.get('coproId');
    const coproId = coproIdParam && /^\d+$/.test(coproIdParam) ? Number(coproIdParam) : null;
    return {
      initialTabId: tabParam ?? null,
      initialCoproId: coproId,
    };
  }, [location.search]);
  const [tabsData, setTabsData] = useState<{
    [key: string]: TabData
  }>({
    'non-contacte': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 } },
    'en-attente': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 } },
    'a-appeler': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 } },
    'demandes-rappel': { status: 'IDLE', data: [], pagination: { currentPage: 0, itemsPerPage: 10, totalResults: 0 }, sortField: '' },
  });
  const tabsRef = useRef<TabsRef>(null);
  const assignedCoprosOnlyRef = useRef(getStoredPreference());
  // Deep link from the dashboard: ?tab=demandes-rappel&due=late|today|tomorrow
  const [recallFilter, setRecallFilter] = useState<DueFilter>(() => parseDueFilter(location.search));
  const recallFilterRef = useRef(recallFilter);

  const getTabFilters = (tabId: string): Record<string, string> => ({
    ...(assignedCoprosOnlyRef.current ? { filterAssignedCoprosOnly: '1' } : {}),
    ...(tabId === RECALL_TAB_ID ? getDueFilters('filterRecallAt', recallFilterRef.current) : {}),
  });

  // Set the current step to MODIFICATION_SINGLE_OWNER_DETAILS when the component mounts
  useEffect(() => {
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS));
    
    // Clean up when the component unmounts
    return () => {
      dispatch(setCurrentStep(null));
    };
  }, [dispatch]);

  // Fetch data for a specific tab
  const fetchTabData = async(tabId: string, step: OwnerStepType, page: number = 0, limit: number = 10, sortField?: string) => {
    if (!company || !jwt || !company.id) {return;}

    setTabsData(prev => ({
      ...prev,
      [tabId]: { 
        ...prev[tabId], 
        status: 'PENDING',
        sortField: sortField || prev[tabId].sortField, 
      },
    }));

    try {
      // Add sortField to API request if available
      const currentSortField = sortField || tabsData[tabId].sortField;
      const response = await fetchOwnersByStep(
        company.id, 
        step, 
        jwt, 
        page + 1, 
        limit,
        currentSortField,
        getTabFilters(tabId),
      );
      
      setTabsData(prev => ({
        ...prev,
        [tabId]: { 
          status: 'SUCCESS', 
          data: response.results,
          pagination: {
            currentPage: page,
            itemsPerPage: limit,
            totalResults: response.pagination.totalResults,
          },
          sortField: currentSortField,
        },
      }));
    } catch (error) {
      console.error(`Error fetching data for tab ${tabId}:`, error);
      setTabsData(prev => ({
        ...prev,
        [tabId]: { ...prev[tabId], status: 'ERROR' },
      }));
    }
  };

  // Handle page change for a specific tab
  const handlePageChange = (tabId: string, step: OwnerStepType) => (page: number, rowsPerPage: number) => {
    fetchTabData(tabId, step, page, rowsPerPage);
  };

  const handleRecallFilterChange = (filter: DueFilter) => {
    recallFilterRef.current = filter;
    setRecallFilter(filter);
    fetchTabData(RECALL_TAB_ID, OWNER_STEPS.TO_RECALL, 0, tabsData[RECALL_TAB_ID].pagination.itemsPerPage);
  };

  // Handle sort change for a specific tab
  const handleSortChange = (tabId: string, step: OwnerStepType) => (sortField: string) => {
    // Reset to first page when changing sort
    fetchTabData(tabId, step, 0, tabsData[tabId].pagination.itemsPerPage, sortField);
  };

  // Fetch initial data for all tabs
  const fetchAllData = async() => {
    if (!company || !jwt || !company.id) {return;}

    for (const tab of TABS) {
      fetchTabData(tab.id, tab.step);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [company, jwt]);

  // Handle interaction form submission
  const handleInteractionSubmit = (_ownerId: number, _formData: InteractionFormData) => {
    // In a real app, this would be an API call to create a new interaction
    // Refresh data after submission
    console.warn('Submitting interaction for owner', _ownerId, _formData);
    fetchAllData();
  };

  // Handle owner edit
  const handleOwnerEdit = (_ownerId: number) => {
    // In a real app, this would open the owner edit form
    console.warn('Editing owner', _ownerId);
    fetchAllData();
  };

  const handleCoproFilterChange = (assignedCoprosOnly: boolean) => {
    assignedCoprosOnlyRef.current = assignedCoprosOnly;
    fetchAllData();
  };

  // Fetch data for specific tabs
  const fetchSpecificTabs = async(tabIds: string[]) => {
    if (!company || !jwt || !company.id) {return;}

    for (const tabId of tabIds) {
      const tab = TABS.find(t => t.id === tabId);
      if (tab) {
        fetchTabData(tab.id, tab.step);
      }
    }
  };

  return (
    <div className="mb-8 overflow-x-scroll rounded-lg bg-white p-4 shadow-md">
      <div className="mb-2 border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-900">Communication aux propriétaires</h2>
        <p className="mt-1 text-sm text-gray-600">Gérez vos communications avec les propriétaires</p>
      </div>
      <ProspectingOpportunitiesOwnerNavTabs />
      <div className="mb-4">
        <CoproAffectationToggle onFilterChange={handleCoproFilterChange} />
      </div>
      <div className="mb-6">
        <p className="mb-4 text-gray-600">
          Gérez efficacement vos communications avec les propriétaires à travers les différentes étapes du processus.
        </p>
        <div className="mb-4 border-l-4 border-blue-500 bg-blue-50 p-4">
          <div className="flex">
            <div className="shrink-0">
              <svg className="h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Utilisez les onglets ci-dessous pour naviguer entre les différentes catégories de propriétaires :
              </p>
              <ul className="mt-1 ml-5 list-disc text-sm text-blue-700">
                <li><strong>Non contacté</strong> : Propriétaires qui n'ont pas encore été contactés</li>
                <li><strong>En attente de retour</strong> : Propriétaires contactés en attente de réponse</li>
                <li><strong>A appeler</strong> : Propriétaires qui n'ont pas répondu au formulaire envoyé par mail</li>
                <li><strong>Demandes de rappels à traiter</strong> : Propriétaires ayant rempli le formulaire de consentement</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <WarningValidationPending />

      <div className="rounded-lg bg-white shadow">
        <Tabs theme={customTheme} ref={tabsRef}>
          {TABS.map((tab) => (
            <Tabs.Item 
              key={tab.id}
              title={`${tab.title} ${tabsData[tab.id].pagination ? `(${tabsData[tab.id].pagination.totalResults})` : ''}`} 
              icon={tab.icon} 
              active={initialTabId === null ? tab.id === 'non-contacte' : tab.id === initialTabId}
            >
              {tab.id === RECALL_TAB_ID && (
                <div className="px-4 pt-4">
                  <DueFilterChips label="Rappels" value={recallFilter} onChange={handleRecallFilterChange} />
                </div>
              )}
              {tabsData[tab.id].status === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {tabsData[tab.id].status === 'ERROR' && (
                <div className="w-full text-center">
                  <Badge
                    mode="error"
                    text="Une erreur est survenue au chargement de vos données"
                    className="mx-10 my-32"
                  />
                </div>
              )}
              {tabsData[tab.id].status === 'SUCCESS' && tabsData[tab.id].data && tabsData[tab.id].data.length > 0 && (
                <div className="p-4">
                  {tab.id === 'non-contacte' && (
                    <NotContactedTable 
                      data={tabsData[tab.id].data} 
                      onOwnerEdit={handleOwnerEdit}
                      apiPagination={true}
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                      onRefresh={() => fetchSpecificTabs(['non-contacte', 'en-attente'])}
                    />
                  )}
                  {tab.id === 'en-attente' && (
                    <PendingTable 
                      data={tabsData[tab.id].data}
                      onOwnerEdit={handleOwnerEdit}
                      apiPagination={true}
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                      onRefresh={() => fetchSpecificTabs(['en-attente'])}
                    />
                  )}
                  {tab.id === 'a-appeler' && (
                    <ToCallTable 
                      data={tabsData[tab.id].data} 
                      onInteractionSubmit={handleInteractionSubmit}
                      apiPagination={true}
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                      onRefresh={() => fetchSpecificTabs(['a-appeler'])}
                    />
                  )}
                  {tab.id === 'demandes-rappel' && (
                    <CallbackRequestsTable 
                      data={tabsData[tab.id].data} 
                      onInteractionSubmit={handleInteractionSubmit}
                      apiPagination={true}
                      totalCount={tabsData[tab.id].pagination.totalResults}
                      onPageChange={handlePageChange(tab.id, tab.step)}
                      currentPage={tabsData[tab.id].pagination.currentPage}
                      currentRowsPerPage={tabsData[tab.id].pagination.itemsPerPage}
                      onRefresh={() => fetchSpecificTabs(['demandes-rappel'])}
                      onSortChange={handleSortChange(tab.id, tab.step)}
                      currentSortField={tabsData[tab.id].sortField}
                    />
                  )}
                </div>
              )}
              {tabsData[tab.id].status === 'SUCCESS' && tabsData[tab.id].data && tabsData[tab.id].data.length === 0 && (
                <div className="w-full text-center">
                  <Badge mode="warning" text={`Aucun propriétaire "${tab.title.toLowerCase()}" trouvé`} className="mx-10 my-32" />
                </div>
              )}
            </Tabs.Item>
          ))}
          <Tabs.Item
            key={DPE_GENERAL_MAILING_TAB_ID}
            title="Envois généraux DPE"
            icon={EnvelopeOpenIcon}
            active={initialTabId === DPE_GENERAL_MAILING_TAB_ID}
          >
            <DpeGeneralMailingsTable initialCoproId={initialCoproId} />
          </Tabs.Item>
        </Tabs>
      </div>
    </div>
  );
}

export default OwnerCommunication; 